import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { MessageCircle, Search, X } from 'lucide-react'
import { useChat } from 'features/Chat/ChatContext'
import { selectChatHeadersInitialized } from 'slices/chatSlice'
import ConversationRow from './ConversationRow'
import type { IChatHeader } from 'features/Chat/Chat.types'

export interface Props {
	onSelect: (chatHeader: IChatHeader) => void
}

// NOTE: rows do not show a "typing…" preview. Doing so needs typing state for
// every thread, and it is only tracked for the thread that is open, inside
// ThreadView, deliberately: keeping it out of ChatProvider is what stops a
// remote keystroke re-rendering every consumer of the chat context. The prop
// that used to be here was never passed by anything and so never did anything.
const ConversationList = ({ onSelect }: Props) => {
	const {
		state: { chatHeaders, userId },
	} = useChat()
	// The inbox fetch's own state, not the context's isLoading (which tracks
	// sending). Without this the list says "No conversations yet" for the whole
	// first fetch.
	const isInitialized = useSelector(selectChatHeadersInitialized)
	const [query, setQuery] = useState('')

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase()
		if (!q) return chatHeaders
		return chatHeaders.filter(header => {
			const preview = header.lastMessage?.message || header.message || ''
			return (
				(header.label || '').toLowerCase().includes(q) || preview.toLowerCase().includes(q)
			)
		})
	}, [chatHeaders, query])

	return (
		<div data-testid="chat-popup-list" className="flex min-h-0 flex-1 flex-col">
			<div className="shrink-0 px-3 pb-2">
				<div className="flex items-center gap-2 rounded-[10px] bg-gray-100 px-2.5 py-1.5">
					<Search className="h-4 w-4 shrink-0 text-gray-400" />
					<input
						value={query}
						onChange={e => setQuery(e.target.value)}
						placeholder="Search"
						aria-label="Search conversations"
						className="min-w-0 flex-1 bg-transparent text-[15px] outline-none placeholder:text-gray-400"
					/>
					{query && (
						<button
							type="button"
							onClick={() => setQuery('')}
							aria-label="Clear search"
							className="shrink-0 text-gray-400 hover:text-gray-600"
						>
							<X className="h-4 w-4" />
						</button>
					)}
				</div>
			</div>

			<div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
				{filtered.length === 0 ? (
					<div className="flex h-full flex-col items-center justify-center gap-2 px-8 text-center">
						<MessageCircle className="h-8 w-8 text-gray-300" />
						<p className="m-0 text-[15px] font-medium text-gray-600">
							{query ? 'No matches' : !isInitialized ? 'Loading…' : 'No conversations yet'}
						</p>
						{!query && isInitialized && (
							<p className="m-0 text-[13px] text-gray-400">
								Start one with the pencil in the top right.
							</p>
						)}
					</div>
				) : (
					<ul className="m-0 list-none divide-y divide-gray-100 p-0">
						{filtered.map(header => (
							<ConversationRow
								key={header._id}
								chatHeader={header}
								userId={userId}
								isUnread={!(header.readByIds || []).includes(userId)}
								onClick={onSelect}
							/>
						))}
					</ul>
				)}
			</div>
		</div>
	)
}

export default ConversationList
